import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type LegalSlug = 'privacy' | 'terms' | 'copyright';

export interface LegalDocument {
  id: string;
  slug: LegalSlug;
  title: string;
  content: string;
  version: number;
  published: boolean;
  updated_at: string;
}

export function useLegalDocument(slug: LegalSlug) {
  return useQuery({
    queryKey: ['legal-document', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      if (error) throw error;
      return data as LegalDocument | null;
    },
  });
}

export function useAllLegalDocuments() {
  return useQuery({
    queryKey: ['legal-documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .order('slug');
      if (error) throw error;
      return (data ?? []) as LegalDocument[];
    },
  });
}

export function useUpdateLegalDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, slug, title, content, published }: Partial<LegalDocument> & { id: string; slug: LegalSlug }) => {
      const { data, error } = await supabase
        .from('legal_documents')
        .update({ title, content, published, version: undefined })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as LegalDocument;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['legal-document', vars.slug] });
      qc.invalidateQueries({ queryKey: ['legal-documents'] });
    },
  });
}
